import { Button, Checkbox, Input, Modal } from '@bfe/moly';
import { replaceVar, runAsyncSafe, web3Navigate } from '@web3/utils';
import { useEffect, useState } from 'react';
import { Case, Switch } from 'react-if';
import { createCloudWallet } from '../../actions';
import { useChainList } from '../../store/chainList';
import { getDefaultIndex } from '../../store/config';
import { WalletCreatePanel, createWalletModalStore, useCreateWalletModal } from '../../store/dialogs';
import { useLocale } from '../../store/i18nMessage';
import { userCloudWalletStore } from '../../store/userCloudWallet';
import { userInfoStore } from '../../store/userInfo';
import { scTrack } from '../../utils/scTrack';
import { USER_AGREEMENT_LINK } from '../../utils/url';
import { ChainIconList } from './ChainIconList';
import createImg from './images/create.png';
import failureImg from './images/failure.png';
import successImg from './images/success.png';
import './style.scss';
import './saveReferral';
import { getCookie } from '../../utils/cookie';
import { walletReferralCookieName } from '../../utils/constants';
import { useReferralCode } from './saveReferral';

export type Web3CreateWalletModalProps = {
  zIndex?: number;
  onCreate?: () => void;
};

export function Web3CreateWalletModal({ zIndex = getDefaultIndex(), onCreate = createWalletModalStore.state.onCreate }: Web3CreateWalletModalProps) {
  const { t, locale } = useLocale();
  const [agree, setAgree] = useState(true);
  const { chainList } = useChainList();
  const { visible, panel } = useCreateWalletModal();
  const [referral, setReferral] = useReferralCode(visible);

  useEffect(() => {
    if (visible && panel === WalletCreatePanel.Create) {
      // 创建云钱包需要上报, 引导弹窗自行创建完成后只显示这个弹窗的成功/失败，不做上报
      scTrack('SpotWeb3CreateBybitWalletPopUpExposure', {});
    }
    if (visible) {
      setReferral(getCookie(walletReferralCookieName) || '');
    }
  }, [visible, panel, setReferral]);

  const handleCreate = async () => {
    createWalletModalStore.setState({
      panel: WalletCreatePanel.Pending,
    });
    const [err] = await runAsyncSafe(createCloudWallet(referral.trim()));
    createWalletModalStore.show({
      panel: err ? WalletCreatePanel.Failed : WalletCreatePanel.Success,
    });
    if (err) {
      // 如果出错上报错误码，web3 里的老逻辑，应该没什么暖用，这是把 神策当效能么。
      scTrack('SpotWeb3CreateFailurePopUpExposure', { result_code: err?.code });
    } else {
      // 成功弹窗出现
      const evmWallet = userCloudWalletStore.getWallet(1);
      onCreate?.();
      scTrack('SpotWeb3CreateSuccessPopUpExposure', {
        UID: userInfoStore.state.userInfo?.id ?? '',
        address: evmWallet?.address.replace(/(.{16}).*(.{16})/, '$1***$2') ?? '',
      });
    }
  };

  return (
    <Modal
      open={visible}
      modal={false}
      title={panel === WalletCreatePanel.Success ? t('created_success') : t('wallet_create')}
      width={480}
      footer={null}
      zIndex={zIndex}
      className="web3-create-wallet-modal moly-theme-component-light"
      onCancel={() => createWalletModalStore.hide()}
    >
      <Switch>
        {/* 创建钱包 */}
        <Case condition={panel === WalletCreatePanel.Create}>
          <div className="text-center py-2">
            <img src={createImg} className="w-[140px] h-[100px] inline-block" alt="" />
            <p className=" text-bds-gray-t2 text-sm mt-6">{t('wallet_create_desc')}</p>
          </div>
          <div className="py-8">
            <Button variant="primary" block disabled={!agree} onClick={handleCreate}>
              {t('to_create')}
            </Button>
            {/* 邀请码，由于@victory1.liu 需求改动，暂时不給用户展示邀请码，邀请逻辑保持不变*/}
            {/* <div className="mt-2">
              <p className="text-bds-gray-t3 text-xs">{t('web3ReferralCode')}</p>
              <Input
                value={referral}
                size="xs"
                placeholder={t('enterReferralCode')}
                onChange={(e) => {
                  setReferral(e.target.value);
                }}
                className="w-full mt-1"
              />
            </div> */}
            <div className="create-footer">
              <Checkbox checked={agree} onChange={() => setAgree(!agree)} />
              <p dangerouslySetInnerHTML={{ __html: t('create_tips1', { url: replaceVar(USER_AGREEMENT_LINK, { locale }) }) }} />
            </div>
          </div>
        </Case>
        {/* 创建中 */}
        <Case condition={panel === WalletCreatePanel.Pending}>
          <div className=" text-center pt-2 pb-10">
            <img src={createImg} className="w-[140px] h-[100px] inline-block" alt="" />
            <p className=" text-bds-gray-t2 text-sm mt-6">{t('wallet_create_loading')}</p>
          </div>
        </Case>
        {/* 创建成功 */}
        <Case condition={panel === WalletCreatePanel.Success}>
          <div className="text-center py-2">
            <div className="py-2 px-4 inline-flex bc-yellow-12 rounded-[100px] text-bds-brand-700-normal text-left items-center">
              <div className="svg-full ltr:mr-2 rtl:ml-2 size-4 flex-none">{iconOrder}</div>
              <p className=" leading-none">{t('create_tips2')}</p>
            </div>
            <div className="my-6">
              <img src={successImg} className="size-24 block mx-auto" alt="" />
              <h6 className="text-lg mt-6 text-bds-static-black">{t('wallet_created_msg')}</h6>
              <p className="mt-2 text-base text-bds-gray-t2">{t('wallet_created_desc')}</p>
            </div>
            <ChainIconList data={chainList.map((c) => c.iconDayUrl)} max={9} size="40px" border-size="3px" spacing="-8px" showOverviewNumber />
          </div>
          <Button
            variant="primary"
            className="mt-8 w-full"
            data-spm-even="SpotWeb3CreateSuccessPopUpClick"
            data-button_name="Explore"
            onClick={() => {
              createWalletModalStore.hide();
              web3Navigate(`/${locale}/web3/balance`);
            }}
          >
            {t('deposit')}
          </Button>
        </Case>

        {/* 创建失败 */}
        <Case condition={panel === WalletCreatePanel.Failed}>
          <div className="py-2 text-center">
            <img src={failureImg} className="w-[120px] h-[88px]" alt="" />
            <p className=" text-bds-gray-t2 text-sm mt-6">{t('wallet_create_fail')}</p>
          </div>
          <Button
            variant="primary"
            className="mt-8 w-full"
            data-spm-even="SpotWeb3CreateFailurePopUpClick"
            data-button_name="Retry"
            onClick={handleCreate}
          >
            {t('retry')}
          </Button>
        </Case>
      </Switch>
    </Modal>
  );
}

const iconOrder = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <path d="M17.75 19.2h-7c-0.464 0-0.909 0.169-1.237 0.469s-0.513 0.707-0.513 1.131c0 0.424 0.184 0.831 0.513 1.131s0.773 0.469 1.237 0.469h7c0.464 0 0.909-0.169 1.237-0.469s0.513-0.707 0.513-1.131c0-0.424-0.184-0.831-0.513-1.131s-0.773-0.469-1.237-0.469zM24.75 3.2h-2.065c-0.361-0.934-1.029-1.742-1.913-2.315s-1.939-0.882-3.022-0.885h-3.5c-1.083 0.003-2.139 0.312-3.022 0.885s-1.552 1.382-1.913 2.315h-2.065c-1.392 0-2.728 0.506-3.712 1.406s-1.538 2.121-1.538 3.394v19.2c0 1.273 0.553 2.494 1.538 3.394s2.32 1.406 3.712 1.406h17.5c1.392 0 2.728-0.506 3.712-1.406s1.538-2.121 1.538-3.394v-19.2c0-1.273-0.553-2.494-1.538-3.394s-2.32-1.406-3.712-1.406zM12.5 4.8c0-0.424 0.184-0.831 0.513-1.131s0.773-0.469 1.237-0.469h3.5c0.464 0 0.909 0.169 1.237 0.469s0.513 0.707 0.513 1.131v1.6h-7v-1.6zM26.5 27.2c0 0.424-0.184 0.831-0.513 1.131s-0.773 0.469-1.237 0.469h-17.5c-0.464 0-0.909-0.169-1.237-0.469s-0.513-0.707-0.513-1.131v-19.2c0-0.424 0.184-0.831 0.513-1.131s0.773-0.469 1.237-0.469h1.75v1.6c0 0.424 0.184 0.831 0.513 1.131s0.773 0.469 1.237 0.469h10.5c0.464 0 0.909-0.169 1.237-0.469s0.513-0.707 0.513-1.131v-1.6h1.75c0.464 0 0.909 0.169 1.237 0.469s0.513 0.707 0.513 1.131v19.2zM21.25 12.8h-10.5c-0.464 0-0.909 0.169-1.237 0.469s-0.513 0.707-0.513 1.131c0 0.424 0.184 0.831 0.513 1.131s0.773 0.469 1.237 0.469h10.5c0.464 0 0.909-0.169 1.237-0.469s0.513-0.707 0.513-1.131c0-0.424-0.184-0.831-0.513-1.131s-0.773-0.469-1.237-0.469z"></path>
  </svg>
);
